import { makeStyles } from '@material-ui/styles';
import { Field, Formik } from 'formik';
import gql from 'graphql-tag';
import React, { useContext, useState } from 'react';

import { Alert } from 'src/common/ui/components';
import { PrimaryButton } from 'src/components/general/PrimaryButton';
import { SecondaryButton } from 'src/components/general/SecondaryButton';
import { ApolloClientContext } from 'src/data/ApolloClientContext';
import { IdSubmission } from 'src/idVerification/types';
import { BooleanControl } from 'src/nightingale/components/ChartProperty/controls/Boolean/BooleanControl';
import { SelectControl } from 'src/nightingale/components/ChartProperty/controls/Select/SelectControl';
import { TextControl } from 'src/nightingale/components/ChartProperty/controls/Text/TextControl';

export const EditIdSubmissionForm: React.FC<{
  item: IdSubmission;
  onCancel: () => void;
  setCurrentSubmission: React.Dispatch<React.SetStateAction<IdSubmission | null>>;
  mutate: () => void;
}> = ({ item, onCancel, setCurrentSubmission, mutate }) => {
  const classes = useStyles();
  const [updateError, setUpdateError] = useState<Error | null>(null);
  const { apolloClient } = useContext(ApolloClientContext);

  const onSave = async (values: Partial<IdSubmission>) => {
    if (!apolloClient) {
      return;
    }

    try {
      await apolloClient.mutate({
        mutation: gql`
          mutation ($id: ID!, $valid: Boolean, $invalidReason: String, $notes: String) {
            staff_updateSubmissionStatus(
              id: $id
              valid: $valid
              invalidReason: $invalidReason
              notes: $notes
            ) {
              id
            }
          }
        `,
        variables: {
          id: values.id,
          valid: values.valid,
          invalidReason: values.valid === false ? values.invalidReason : null,
          notes: values.notes,
        },
      });

      mutate();
    } catch (e) {
      setUpdateError(e);
    }
  };

  return (
    <div className={classes.formContainer}>
      {updateError && (
        <Alert title="Error updating verification status" error={updateError} severity="error" />
      )}
      <Formik
        initialValues={item}
        onSubmit={(values, actions) =>
          onSave(values).finally(() => {
            actions.setSubmitting(false);
            setCurrentSubmission(null);
          })
        }
      >
        {({ handleSubmit, isSubmitting, dirty, values }) => {
          return (
            <form className={classes.form} onSubmit={handleSubmit}>
              <Field label="Is this ID valid?" name="valid" component={FormBooleanControl} />
              {values.valid === false && (
                <Field
                  label="Why isn't this ID valid?"
                  name="invalidReason"
                  component={FormSelectControl}
                />
              )}
              <Field label="Notes" name="notes" multiline component={FormTextControl} />

              <div className={classes.buttons}>
                <SecondaryButton onClick={() => onCancel()}>Cancel</SecondaryButton>
                <PrimaryButton
                  className={classes.button}
                  disabled={isSubmitting || !dirty}
                  type="submit"
                >
                  Save
                </PrimaryButton>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

const FormBooleanControl = ({ field, form, label }) => (
  <BooleanControl
    label={label}
    name={field.name}
    onChangeValue={newVal => {
      form.setFieldValue(field.name, newVal);
    }}
    value={field.value}
  />
);

const INVALID_ID_REASON_OPTIONS = (
  [
    'Photo too blurry',
    'Not acceptable form',
    'Expired ID',
    'Photo of phone',
    "Names don't match (e.g. maiden name)",
    "ID photo doesn't match profile photo",
    'Profile photo unacceptable (face covered, not well lit, photo of photo)',
    'Insufficient ID on its own',
  ] as const
).map(reason => ({
  label: reason,
  value: reason,
}));

const FormSelectControl = ({ field, form, label }) => (
  <SelectControl
    label={label}
    name={field.name}
    onChangeValue={(newVal: (typeof INVALID_ID_REASON_OPTIONS)[number] | null) => {
      form.setFieldValue(field.name, newVal?.value || null);
    }}
    value={field.value ? { label: field.value, value: field.value } : null}
    options={INVALID_ID_REASON_OPTIONS}
  />
);

const FormTextControl = ({ field, form, label }) => (
  <TextControl
    label={label}
    multiline
    name={field.name}
    onChangeValue={newVal => {
      form.setFieldValue(field.name, newVal);
    }}
    value={field.value || ''}
  />
);

const useStyles = makeStyles({
  formContainer: {
    width: '100%',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    '& .MuiFormControl-root': {
      marginBottom: 16,
    },
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 20,
  },
  button: {
    marginLeft: 0,
  },
});
